import { sendCopyWishlist } from "~/features/wishlist/api/CopyWishlist";
import { sendCopySharedWishlist } from "~/features/wishlist/api/CopySharedWishlist";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { COPY_WISHLIST_STATUS_PARAM } from "~/features/wishlist/wishlistUrlParams";
import {
  LOGIN_ACTION_COPY_SHARED_WISHLIST,
  LOGIN_ACTION_COPY_WISHLIST,
  LOGIN_ACTION_COPY_WISHLIST_ID,
  LOGIN_ACTION_REDIRECT,
  LOGIN_ACTION_REDIRECT_URL,
  URL_PARAM_ON_LOGIN_ACTION,
  URL_PARAM_OPEN_LOGIN_POPUP,
} from "~/features/auth/login/loginConstants";
import { invalidateNavigationCache } from "~/features/navigation/redux/navigationThunks";
import { PASSWORD_CHANGE_PAGE, WISHLIST_PAGE } from "~/utils/url/staticUrls";
import { rememberAccountSelectionReturnUrl } from "~/utils/accountSelectionContinuation";

const LOGIN_PARAMS = [URL_PARAM_OPEN_LOGIN_POPUP, URL_PARAM_ON_LOGIN_ACTION];

export function isLoginPopupInitiallyOpen() {
  const params = new URLSearchParams(window.location.search);

  if (params.get(URL_PARAM_OPEN_LOGIN_POPUP) === "true") {
    return true;
  }

  return window.sessionStorage.getItem(URL_PARAM_OPEN_LOGIN_POPUP) === "true";
}
export function isLoginTimeoutErrorVisible() {
  const params = new URLSearchParams(window.location.search);

  return params.get("timeout") === "true";
}

export function isForcedLoginTour() {
  return window.SCHA.config.forceLoginPopup ?? false;
}
export function isForcedLogin() {
  const forceLogin = window.SCHA.config.forceLoginPopup ?? false;
  return forceLogin && !window.location.pathname.endsWith(PASSWORD_CHANGE_PAGE);
}

export function defaultLoginAction(redirectUrl?: string) {
  rememberAccountSelectionReturnUrl(redirectUrl);
  if (redirectUrl) {
    redirectToUrl(redirectUrl);
    return;
  }
  const params = new URLSearchParams(window.location.search);
  LOGIN_PARAMS.forEach((param) => params.delete(param));
  const queryString = params.toString();
  let url = window.location.pathname;
  url = queryString ? url + "?" + queryString.toString() : url;
  redirectToUrl(url);
}

async function copyWishlist() {
  const params = new URLSearchParams(window.location.search);
  const wishlistId = params.get(LOGIN_ACTION_COPY_WISHLIST_ID);

  if (!wishlistId) {
    console.error("Missing wishlist id, falling back to default login behavior");
    defaultLoginAction();
    return;
  }

  let url = WISHLIST_PAGE;

  try {
    const response = await sendCopyWishlist({ wishlistId });
    if (response.ok) {
      url += `?${COPY_WISHLIST_STATUS_PARAM}=true&extended=true`;
    } else {
      url += `?${COPY_WISHLIST_STATUS_PARAM}=false`;
    }
  } catch (err) {
    url += `?${COPY_WISHLIST_STATUS_PARAM}=false`;
  } finally {
    redirectToUrl(url);
  }
}

async function copySharedWishlist() {
  const params = new URLSearchParams(window.location.search);
  const wishlistId = params.get(LOGIN_ACTION_COPY_WISHLIST_ID);

  if (!wishlistId) {
    console.error("Missing wishlist id, falling back to default login behavior");
    defaultLoginAction();
    return;
  }

  let url = WISHLIST_PAGE;

  try {
    const response = await sendCopySharedWishlist({ wishlistId });
    if (response === true) {
      url += `?${COPY_WISHLIST_STATUS_PARAM}=true&itemsRemoved=true&extended=true`;
    } else {
      url += `?${COPY_WISHLIST_STATUS_PARAM}=true&extended=true`;
    }
  } catch (err) {
    url += `?${COPY_WISHLIST_STATUS_PARAM}=false`;
  } finally {
    redirectToUrl(url);
  }
}

const ON_LOGIN_ACTIONS: Dict<() => void> = {
  [LOGIN_ACTION_COPY_WISHLIST]: copyWishlist,
  [LOGIN_ACTION_COPY_SHARED_WISHLIST]: copySharedWishlist,
  [LOGIN_ACTION_REDIRECT]: redirectFromParam,
};
function redirectFromParam() {
  const params = new URLSearchParams(window.location.search);
  const url = params.get(LOGIN_ACTION_REDIRECT_URL);
  if (url) {
    redirectToUrl(url);
  } else {
    defaultLoginAction();
  }
}

export function doOnLoginAction(redirectUrl?: string) {
  const params = new URLSearchParams(window.location.search);

  invalidateNavigationCache();
  const actionName = params.get(URL_PARAM_ON_LOGIN_ACTION) ?? "";
  const action = ON_LOGIN_ACTIONS[actionName];
  if (action) {
    action();
  } else {
    defaultLoginAction(redirectUrl);
  }
}

export const LOGOUT_SUCCESS_KEY = "logoutSuccess";

export const markLogoutSuccessful = () => window.sessionStorage.setItem(LOGOUT_SUCCESS_KEY, "true");
